// ArtistUpgradeModal — shown when an artist's 30-day trial has expired
// (or they lapsed a subscription) and they try to edit their profile,
// upload flash, or access leads.
//
// Deliberately separate from the customer UpgradeModal because:
//   - Different audience (professionals, not hobbyists)
//   - Different price points ($19/mo vs $4.99/mo)
//   - Different value proposition
//   - Room for a monthly vs yearly comparison
//
// If the user still has days left on their artist trial, the calling code
// should open the wizard directly — NOT open this modal.

import React, { useState } from "react";
import toast from "react-hot-toast";
import { startCheckout } from "./api";
import { PLANS } from "./constants";

// Order matters: monthly first (anchor), yearly second (value play).
const MODAL_PLANS = [PLANS.artist_pro, PLANS.artist_pro_yearly].filter(Boolean);

export default function ArtistUpgradeModal({
  open,
  onClose,
  artistTrialDaysLeft = 0,
  trialStarted = false,
  reason = "artist_profile",
}) {
  const [loadingPlan, setLoadingPlan] = useState(null);

  if (!open) return null;

  async function handleChoose(plan) {
    try {
      setLoadingPlan(plan);
      await startCheckout(plan);
      // startCheckout navigates away on success.
    } catch (err) {
      console.error("[artist-upgrade-modal] checkout failed", err);
      toast.error(err.message || "Could not open checkout");
      setLoadingPlan(null);
    }
  }

  // Headline + supporting copy depends on trial state.
  let headline;
  let subcopy;
  if (!trialStarted) {
    // Shouldn't normally reach this branch — the entry gate starts the trial
    // silently — but keep copy safe just in case.
    headline = "Become a Tattune artist";
    subcopy =
      "Get your public artist profile, upload your flash gallery, and start receiving leads from Tattune users.";
  } else if (artistTrialDaysLeft > 0) {
    headline = "Keep going after your trial";
    subcopy = `You've got ${artistTrialDaysLeft} day${artistTrialDaysLeft === 1 ? "" : "s"} left on your Artist trial. Lock in your profile and keep the leads flowing.`;
  } else {
    headline = "Your artist trial has ended";
    subcopy =
      "Subscribe to keep your public profile live, your flash gallery visible, and incoming leads coming to you.";
  }

  const reasonCopy = {
    artist_profile: "Edit or publish your studio profile",
    flash_upload: "Upload and manage your flash gallery",
    leads: "Access leads sent from Tattune users",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.78)",
        display: "grid",
        placeItems: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "#0b0b0b",
          border: "1px solid #262626",
          borderRadius: 16,
          padding: 24,
          color: "#eaeaea",
          boxShadow: "0 24px 80px rgba(0,0,0,0.65)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#c92d2d",
                marginBottom: 6,
              }}
            >
              Artist Pro
            </div>
            <h2 style={{ fontSize: 22, margin: 0, letterSpacing: "0.01em" }}>
              {headline}
            </h2>
            <p style={{ fontSize: 13, color: "#a8a8a8", margin: "6px 0 0" }}>
              {subcopy}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              color: "#888",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {reasonCopy[reason] && (
          <div
            style={{
              background: "rgba(201, 45, 45, 0.08)",
              border: "1px solid rgba(201, 45, 45, 0.3)",
              color: "#f2c9c9",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            <strong>Locked behind Artist Pro:</strong> {reasonCopy[reason]}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MODAL_PLANS.map((plan, idx) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              loading={loadingPlan === plan.id}
              disabled={loadingPlan !== null && loadingPlan !== plan.id}
              onChoose={() => handleChoose(plan.id)}
              highlighted={idx === 1} // yearly = highlighted "best value"
            />
          ))}
        </div>

        <p
          style={{
            fontSize: 11,
            color: "#707070",
            textAlign: "center",
            marginTop: 16,
          }}
        >
          Payments by Stripe. Cancel anytime from your account page.
        </p>
      </div>
    </div>
  );
}

function PlanCard({ plan, loading, disabled, onChoose, highlighted }) {
  return (
    <button
      type="button"
      onClick={onChoose}
      disabled={loading || disabled}
      style={{
        textAlign: "left",
        background: highlighted
          ? "linear-gradient(180deg, rgba(201,45,45,0.18) 0%, #141414 60%)"
          : "#141414",
        border: highlighted
          ? "1px solid rgba(201,45,45,0.55)"
          : "1px solid #2b2b2b",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: loading || disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: "inherit",
        transition: "border-color 120ms",
        position: "relative",
      }}
    >
      {highlighted && (
        <span
          style={{
            position: "absolute",
            top: -10,
            right: 12,
            background: "#c92d2d",
            color: "#fff",
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          Best value
        </span>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>{plan.name}</div>
        <div style={{ fontSize: 14, color: "#f5f5f5" }}>
          <strong>{plan.price}</strong>
          <span style={{ color: "#9d9d9d", fontSize: 12, marginLeft: 4 }}>
            {plan.cadence}
          </span>
        </div>
      </div>
      <ul
        style={{
          fontSize: 12,
          color: "#c2c2c2",
          margin: "8px 0 0",
          paddingLeft: 16,
          lineHeight: 1.5,
        }}
      >
        {plan.features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      {loading && (
        <div style={{ fontSize: 12, color: "#c92d2d", marginTop: 10 }}>
          Opening Stripe…
        </div>
      )}
    </button>
  );
}
